<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import { onMount } from 'svelte';
  import type {
    Project,
    ProjectsByStatus,
    ProjectStatus,
    CalendarEvent,
    MonthRange,
    DisplayFilter,
  } from '../lib/types';
  import { showProjects, showEvents } from '../lib/types';
  import {
    getShortMonthName,
    getShortMonthWithYear,
    getYearFromMonthKey,
    isCurrentMonth,
    isFutureMonth,
    getWeekKey,
    getWeekStart,
    getWeekEnd,
    getWeekDisplayRange,
    getWeekNumber,
    categoryIcons,
    statusAccentColors,
    eventsForMonth,
    isMultiDayEvent,
    eventTotalDays,
    parseCalendarDate,
    progressStyle,
    progressComplete,
    sortEventsForDisplay,
    formatEventTimeShort,
  } from '../lib/utils';
  import { showProjectTooltip, hideProjectTooltip } from '../lib/stores/projectTooltip';
  import { setupMaxVisible, MAX_VISIBLE_PRESETS } from '../lib/useMaxVisible';
  import {
    draggedProject,
    draggedEvent,
    openProjectRail,
    openProjectModalForCreate,
    openEventModal,
    openEventModalForEdit,
    openMonthRail,
    openEventsRailForMonth,
    requestProjectMove,
    requestEventMove,
  } from '../lib/stores/ui';
  import { showProjectCellPopover, hideProjectCellPopover } from '../lib/stores/projectCellPopover';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    visibleMonths: string[];
    projectsByMonth: Record<string, ProjectsByStatus>;
    events: CalendarEvent[];
    currentMonth: string;
    range: MonthRange;
    display?: DisplayFilter | null;
  }

  let {
    visibleMonths,
    projectsByMonth,
    events,
    currentMonth,
    range = 'quarter',
    display = null,
  }: Props = $props();

  // Range options (ordered: quarter, 3 months, 6 months, year)
  const rangeOptions: { value: MonthRange; label: string }[] = [
    { value: '1-month', label: '1 Month' },
    { value: 'quarter', label: 'Quarter' },
    { value: '3-months', label: '3 months' },
    { value: '6-months', label: '6 months' },
    { value: 'year', label: 'Year' },
  ];

  function setRange(newRange: MonthRange) {
    if (newRange === range) return;

    const params = new URLSearchParams();
    params.set('view', 'timeline');
    if (newRange !== 'quarter') {
      params.set('range', newRange);
    }

    router.get(`/month/${currentMonth}?${params.toString()}`, {}, { preserveScroll: true });
  }

  // Group months by year for year headers
  let monthsByYear = $derived(() => {
    const groups: { year: number; months: string[] }[] = [];
    let currentGroup: { year: number; months: string[] } | null = null;

    for (const monthKey of visibleMonths) {
      const year = getYearFromMonthKey(monthKey);

      if (!currentGroup || currentGroup.year !== year) {
        currentGroup = { year, months: [] };
        groups.push(currentGroup);
      }
      currentGroup.months.push(monthKey);
    }

    return groups;
  });

  // Get all projects for a month
  function getAllProjects(monthKey: string): Project[] {
    const data = projectsByMonth[monthKey] || { planned: [], active: [], almost: [], done: [] };
    return [...(data.active || []), ...(data.planned || []), ...(data.done || [])];
  }

  // Get events for a specific month
  function getMonthEvents(monthKey: string): CalendarEvent[] {
    const [year, month] = monthKey.split('-').map(Number);
    const monthStart = new Date(Date.UTC(year, month - 1, 1));
    const monthEnd = new Date(Date.UTC(year, month, 0));

    return eventsForMonth(events, monthKey);
  }

  // Group events by week within a month
  interface WeekGroup {
    weekKey: string;
    label: string;
    events: CalendarEvent[];
  }

  function getEventsGroupedByWeek(monthKey: string): WeekGroup[] {
    const monthEvents = getMonthEvents(monthKey);
    if (monthEvents.length === 0) return [];

    const weekMap = new Map<string, CalendarEvent[]>();

    for (const event of monthEvents) {
      const eventDate = parseCalendarDate(event.date);
      const weekKey = getWeekKey(eventDate);
      if (!weekMap.has(weekKey)) {
        weekMap.set(weekKey, []);
      }
      weekMap.get(weekKey)!.push(event);
    }

    // Sort by week key and build groups
    const sortedKeys = [...weekMap.keys()].sort();
    return sortedKeys.map((weekKey) => ({
      weekKey,
      label: getWeekDisplayRange(weekKey),
      events: weekMap.get(weekKey)!,
    }));
  }

  // Get truncated week groups that fit within maxVisible events
  function getTruncatedWeekGroups(
    weekGroups: WeekGroup[],
    maxEvents: number,
  ): { groups: WeekGroup[]; hiddenCount: number; totalCount: number } {
    const totalCount = weekGroups.reduce((sum, g) => sum + g.events.length, 0);
    if (totalCount <= maxEvents) {
      return { groups: weekGroups, hiddenCount: 0, totalCount };
    }

    let remaining = maxEvents;
    const truncatedGroups: WeekGroup[] = [];

    for (const group of weekGroups) {
      if (remaining <= 0) break;
      if (group.events.length <= remaining) {
        truncatedGroups.push(group);
        remaining -= group.events.length;
      } else {
        truncatedGroups.push({
          ...group,
          events: group.events.slice(0, remaining),
        });
        remaining = 0;
      }
    }

    return {
      groups: truncatedGroups,
      hiddenCount: totalCount - maxEvents,
      totalCount,
    };
  }

  // Month state
  function getMonthState(monthKey: string): 'past' | 'current' | 'future' {
    if (isCurrentMonth(monthKey)) return 'current';
    if (isFutureMonth(monthKey)) return 'future';
    return 'past';
  }

  // Navigate to month focus view
  function navigateToMonth(monthKey: string) {
    router.get(`/month/${monthKey}?view=focus`, {}, { preserveScroll: true });
  }

  function handleAddProject(monthKey: string) {
    openProjectModalForCreate({
      schedulingState: 'scheduled',
      monthKey: monthKey,
      status: 'planned',
    });
  }

  function handleAddEvent(monthKey: string) {
    openEventModal(`${monthKey}-01`);
  }

  // Double-click on empty area to add
  function handleProjectsDoubleClick(e: MouseEvent, monthKey: string) {
    const target = e.target as HTMLElement;
    if (
      target.closest('.project-bar') ||
      target.closest('.more-bar') ||
      target.closest('.add-inline-btn')
    )
      return;
    handleAddProject(monthKey);
  }

  function handleEventsDoubleClick(e: MouseEvent, monthKey: string) {
    const target = e.target as HTMLElement;
    if (
      target.closest('.event-bar') ||
      target.closest('.more-bar') ||
      target.closest('.add-inline-btn')
    )
      return;
    handleAddEvent(monthKey);
  }

  // Project cell popover handlers
  function handleMoreBarEnter(e: MouseEvent, monthKey: string, projects: Project[]) {
    showProjectCellPopover(
      projects,
      getShortMonthWithYear(monthKey),
      e.currentTarget as HTMLElement,
    );
  }

  function handleMoreBarLeave() {
    hideProjectCellPopover();
  }

  // Dynamic max visible + responsive column count using ResizeObserver
  let timelineGrid: HTMLElement;
  let maxVisibleProjects = $state<number>(5);
  let maxVisibleEvents = $state<number>(4);
  let projectColumns = $state<number>(2);

  // Minimum column width (px) before dropping to fewer columns
  const MIN_COL_WIDTH = 120;
  let projectMaxVisibleCleanup: (() => void) | null = null;

  function setupProjectMaxVisible(container: HTMLElement, columns: number) {
    if (projectMaxVisibleCleanup) {
      projectMaxVisibleCleanup();
      projectMaxVisibleCleanup = null;
    }
    projectMaxVisibleCleanup = setupMaxVisible(
      container,
      {
        itemHeight: 19.5,
        gap: 2,
        containerPadding: 12,
        columns: columns,
        minVisible: 1,
      },
      (max) => {
        maxVisibleProjects = max;
      },
    );
  }

  onMount(() => {
    const cleanups: (() => void)[] = [];

    // Observe the column containers (not the content divs) so we measure available space
    const projectsColumn = timelineGrid?.querySelector('.month-column') as HTMLElement;
    const eventsColumn = timelineGrid?.querySelector('.events-column') as HTMLElement;

    if (projectsColumn) {
      // Responsive column count based on column width
      const colObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const w = projectsColumn.clientWidth - 12; // subtract padding
          const newCols = Math.max(1, Math.min(2, Math.floor(w / MIN_COL_WIDTH)));
          if (newCols !== projectColumns) {
            projectColumns = newCols;
            setupProjectMaxVisible(projectsColumn, newCols);
          }
        });
      });
      colObserver.observe(projectsColumn);
      cleanups.push(() => colObserver.disconnect());

      setupProjectMaxVisible(projectsColumn, projectColumns);
    }

    if (eventsColumn) {
      cleanups.push(
        setupMaxVisible(
          eventsColumn,
          {
            itemHeight: 20,
            gap: 2,
            containerPadding: 12,
            moreButtonHeight: 18,
            minVisible: 1,
          },
          (max) => {
            maxVisibleEvents = max;
          },
        ),
      );
    }

    return () => {
      cleanups.forEach((fn) => fn());
      if (projectMaxVisibleCleanup) projectMaxVisibleCleanup();
    };
  });

  // Drag and drop for projects
  let draggingProjectId = $state<string | null>(null);
  let projectDropTarget = $state<string | null>(null);

  function handleDragStart(e: DragEvent, project: Project) {
    hideProjectTooltip(true);
    draggingProjectId = project.id;
    draggedProject.set(project);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/project', String(project.id));
    }
  }

  function handleDragEnd() {
    draggingProjectId = null;
    draggedProject.set(null);
  }

  function handleProjectDragOver(e: DragEvent, monthKey: string) {
    e.preventDefault();
    if ($draggedProject) {
      projectDropTarget = monthKey;
    }
  }

  function handleProjectDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      projectDropTarget = null;
    }
  }

  function handleProjectDrop(e: DragEvent, monthKey: string) {
    e.preventDefault();
    projectDropTarget = null;

    // Only process if a project (not an event) is being dragged
    if (!$draggedProject) return;

    const projectId =
      e.dataTransfer?.getData('application/project') || e.dataTransfer?.getData('text/plain');
    if (projectId) {
      requestProjectMove(projectId, $draggedProject, 'month', { monthKey, status: 'planned' });
    }
  }

  // Drag and drop for events
  let draggingEventId = $state<string | null>(null);
  let eventDropTarget = $state<string | null>(null);

  function handleEventDragStart(e: DragEvent, event: CalendarEvent) {
    draggingEventId = event.id;
    draggedEvent.set(event.id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/event', String(event.id));
    }
  }

  function handleEventDragEnd() {
    draggingEventId = null;
    draggedEvent.set(null);
  }

  function handleEventDragOver(e: DragEvent, monthKey: string) {
    e.preventDefault();
    if ($draggedEvent) {
      eventDropTarget = monthKey;
    }
  }

  function handleEventDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      eventDropTarget = null;
    }
  }

  function handleEventDrop(e: DragEvent, monthKey: string) {
    e.preventDefault();
    eventDropTarget = null;

    // Only process if an event (not a project) is being dragged
    if (!$draggedEvent) return;

    const eventId =
      e.dataTransfer?.getData('application/event') || e.dataTransfer?.getData('text/plain');
    if (eventId) {
      const eventObj = events.find((candidate) => candidate.id === eventId);
      if (eventObj) {
        requestEventMove(eventId, eventObj, 'month', { monthKey });
      } else {
        router.post(
          `/calendar_events/${eventId}/move`,
          {
            date: `${monthKey}-01`,
          },
          {
            preserveScroll: true,
          },
        );
      }
    }
  }
</script>

<div class="timeline-container">
  <!-- Header with range selector -->
  <div class="timeline-header">
    <span class="header-title">Monthly Timeline</span>
    <div class="range-selector">
      {#each rangeOptions as opt}
        <button
          type="button"
          class="range-btn"
          class:active={range === opt.value}
          onclick={() => setRange(opt.value)}
        >
          {opt.label}
        </button>
      {/each}
    </div>
  </div>

  <!-- Timeline grid -->
  <div class="timeline-scroll" bind:this={timelineGrid}>
    <div class="timeline-grid">
      <!-- Year headers row -->
      <div class="year-headers">
        {#each monthsByYear() as group (group.year)}
          <div class="year-header" style="flex: {group.months.length}">
            <span class="year-name">{group.year}</span>
          </div>
        {/each}
      </div>

      <!-- Month headers row -->
      <div class="month-headers">
        {#each visibleMonths as monthKey (monthKey)}
          {@const state = getMonthState(monthKey)}
          {@const isCurrent = state === 'current'}

          <button
            type="button"
            class="month-header"
            class:past={state === 'past'}
            class:current={isCurrent}
            class:future={state === 'future'}
            onclick={() => navigateToMonth(monthKey)}
          >
            <span class="month-name">{getShortMonthName(monthKey)}</span>
            {#if isCurrent}
              <span class="current-dot"></span>
            {/if}
          </button>
        {/each}
      </div>

      <!-- Projects row -->
      {#if showProjects(display)}
        <div class="projects-row">
          {#each visibleMonths as monthKey (monthKey)}
            {@const state = getMonthState(monthKey)}
            {@const projects = getAllProjects(monthKey)}
            {@const visibleProjects = projects.slice(0, maxVisibleProjects)}
            {@const hiddenCount = Math.max(0, projects.length - maxVisibleProjects)}

            <div
              class="month-column"
              class:past={state === 'past'}
              class:current={state === 'current'}
              class:future={state === 'future'}
              class:drop-target={projectDropTarget === monthKey}
              role="group"
              ondragover={(e) => handleProjectDragOver(e, monthKey)}
              ondragleave={handleProjectDragLeave}
              ondrop={(e) => handleProjectDrop(e, monthKey)}
              ondblclick={(e) => handleProjectsDoubleClick(e, monthKey)}
            >
              <div
                class="projects-stack"
                style="grid-template-columns: repeat({projectColumns}, 1fr);"
              >
                {#each visibleProjects as project (project.id)}
                  <button
                    type="button"
                    class="project-bar {statusAccentColors[project.status]}"
                    style={progressStyle(project)}
                    data-progress-complete={progressComplete(project) || undefined}
                    class:dragging={draggingProjectId === project.id}
                    draggable={!$viewport.isMobile}
                    ondragstart={(e) => handleDragStart(e, project)}
                    ondragend={handleDragEnd}
                    onclick={() => {
                      hideProjectTooltip(true);
                      openProjectRail(project.id);
                    }}
                    onmouseenter={(e) =>
                      showProjectTooltip(project, e.currentTarget as HTMLElement)}
                    onmouseleave={() => hideProjectTooltip()}
                    title="{project.title} ({project.status}){project.multiWeek
                      ? ` — ${project.totalWeeks} weeks`
                      : ''}"
                  >
                    <span class="bar-category">{categoryIcons[project.category]}</span>
                    {#if project.isPriority}
                      <span class="bar-star">★</span>
                    {/if}
                    <span class="bar-title">{project.title}</span>
                    {#if project.multiWeek && project.totalWeeks}
                      <span class="bar-span">{project.totalWeeks}w</span>
                    {/if}
                  </button>
                {/each}
                {#if hiddenCount > 0}
                  <span
                    class="more-bar"
                    role="note"
                    onmouseenter={(e) => handleMoreBarEnter(e, monthKey, projects)}
                    onmouseleave={handleMoreBarLeave}
                  >
                    +{hiddenCount} more
                  </span>
                {/if}
              </div>
              <button
                type="button"
                class="add-inline-btn hover-reveal"
                onclick={() => handleAddProject(monthKey)}
                title="Add project"
              >
                +
              </button>
            </div>
          {/each}
        </div>
      {/if}

      <!-- Events row -->
      {#if showEvents(display)}
        <div class="events-row">
          {#each visibleMonths as monthKey (monthKey)}
            {@const state = getMonthState(monthKey)}
            {@const weekGroups = getEventsGroupedByWeek(monthKey)}
            {@const truncated = getTruncatedWeekGroups(weekGroups, maxVisibleEvents)}

            <div
              class="events-column"
              class:current={state === 'current'}
              class:drop-target={eventDropTarget === monthKey}
              role="group"
              ondragover={(e) => handleEventDragOver(e, monthKey)}
              ondragleave={handleEventDragLeave}
              ondrop={(e) => handleEventDrop(e, monthKey)}
              ondblclick={(e) => handleEventsDoubleClick(e, monthKey)}
            >
              {#if weekGroups.length > 0}
                <div class="events-area">
                  {#each truncated.groups as group (group.weekKey)}
                    <div class="week-group">
                      <div class="week-group-label">{group.label}</div>
                      {#each group.events as event (event.id)}
                        {@const timeStr = formatEventTimeShort(event)}
                        <button
                          type="button"
                          class="event-bar event-{event.type}"
                          class:dragging={draggingEventId === event.id}
                          draggable={!$viewport.isMobile}
                          ondragstart={(e) => handleEventDragStart(e, event)}
                          ondragend={handleEventDragEnd}
                          onclick={() => openEventModalForEdit(event)}
                          title="{timeStr ? `${timeStr} ` : ''}{event.title} ({event.date})"
                        >
                          <span class="event-day"
                            >{parseCalendarDate(event.date).getUTCDate()}{#if timeStr}
                              · {timeStr}{/if}</span
                          >
                          <span class="event-title">{event.title}</span>
                          {#if isMultiDayEvent(event)}
                            <span class="bar-span">{eventTotalDays(event)}d</span>
                          {/if}
                        </button>
                      {/each}
                    </div>
                  {/each}
                  {#if truncated.hiddenCount > 0}
                    <button
                      type="button"
                      class="more-bar"
                      onclick={() =>
                        openEventsRailForMonth(monthKey, getShortMonthWithYear(monthKey))}
                    >
                      +{truncated.hiddenCount} more
                    </button>
                  {/if}
                </div>
              {:else}
                <div class="empty-events">—</div>
              {/if}
              <button
                type="button"
                class="add-inline-btn event-add-btn hover-reveal"
                onclick={() => handleAddEvent(monthKey)}
                title="Add event"
              >
                +
              </button>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .timeline-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0.375rem;
    gap: 0;
    overflow: hidden;
    background: #f9fafb;
  }

  /* Header */
  .timeline-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.375rem 0.5rem;
    background: white;
    border-radius: 0.375rem 0.375rem 0 0;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .header-title {
    font-size: 0.75rem;
    font-weight: 700;
    color: #374151;
  }

  .range-selector {
    display: flex;
    background: #f9fafb;
    border-radius: 0.25rem;
    padding: 0.125rem;
    border: 1px solid #e5e7eb;
  }

  .range-btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .range-btn:hover {
    color: #374151;
    background: white;
  }

  .range-btn.active {
    color: #1d4ed8;
    background: white;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  /* Timeline scroll area - fills remaining space */
  .timeline-scroll {
    flex: 1;
    overflow-x: auto;
    overflow-y: hidden;
    background: white;
    border-radius: 0 0 0.375rem 0.375rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .timeline-grid {
    display: flex;
    flex-direction: column;
    min-width: 100%;
    height: 100%;
  }

  /* Year headers */
  .year-headers {
    display: flex;
    flex-shrink: 0;
  }

  .year-header {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    background: #f3f4f6;
    border-right: 1px solid #e5e7eb;
  }

  .year-header:last-child {
    border-right: none;
  }

  .year-name {
    font-size: 0.6875rem;
    font-weight: 700;
    color: #374151;
    letter-spacing: 0.02em;
  }

  /* Month headers */
  .month-headers {
    display: flex;
    flex-shrink: 0;
    border-bottom: 1px solid #e5e7eb;
  }

  .month-header {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.375rem 0.25rem;
    background: none;
    border: none;
    border-right: 1px solid #f3f4f6;
    cursor: pointer;
    transition: background 0.15s;
    position: relative;
  }

  .month-header:last-child {
    border-right: none;
  }

  .month-header:hover {
    background: #f3f4f6;
  }

  .month-header.past {
    opacity: 0.6;
  }

  .month-header.current {
    background: #eff6ff;
  }

  .month-name {
    font-size: 0.75rem;
    font-weight: 700;
    color: #374151;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  .current-dot {
    position: absolute;
    top: 0.125rem;
    right: 0.25rem;
    width: 6px;
    height: 6px;
    background: #3b82f6;
    border-radius: 50%;
  }

  /* Projects row - 1/3 of space */
  .projects-row {
    display: flex;
    flex: 1;
    min-height: 0;
  }

  .month-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0.375rem;
    border-right: 1px solid #f3f4f6;
    transition: all 0.15s;
    position: relative;
    overflow: hidden;
  }

  .month-column:last-child {
    border-right: none;
  }

  .month-column.past {
    background: #fafafa;
  }

  .month-column.current {
    background: #eff6ff;
    box-shadow: inset 0 0 0 1px #93c5fd;
  }

  .month-column.future {
    background: #fdf9ff;
  }

  .month-column.drop-target {
    background: #dbeafe !important;
    box-shadow: inset 0 0 0 2px #3b82f6 !important;
  }

  .projects-stack {
    display: grid;
    /* grid-template-columns set via inline style for responsive column count */
    grid-auto-rows: min-content;
    gap: 0.125rem;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    align-content: start;
  }

  .project-bar {
    display: flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.125rem 0.25rem;
    border-radius: 0.1875rem;
    font-size: 0.5625rem;
    background: #f9fafb;
    border: 1.5px solid var(--chip-border-color, #e5e7eb);
    cursor: grab;
    text-align: left;
    transition: all 0.15s;
    min-width: 0;
    flex-shrink: 0;
  }

  .bar-category {
    font-size: 0.5rem;
    flex-shrink: 0;
    line-height: 1;
  }

  .project-bar:hover {
    background: #f3f4f6;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  .project-bar.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .bar-star {
    color: #facc15;
    font-size: 0.5rem;
    flex-shrink: 0;
  }

  .bar-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #374151;
    min-width: 0;
  }

  .bar-span {
    flex-shrink: 0;
    font-size: 0.5rem;
    font-weight: 600;
    font-family: ui-monospace, monospace;
    color: #1d4ed8;
    background: rgba(59, 130, 246, 0.15);
    padding: 0 0.1875rem;
    border-radius: 0.125rem;
    letter-spacing: -0.02em;
  }

  .more-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.125rem 0.25rem;
    font-size: 0.5rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: 1px dashed #9ca3af;
    border-radius: 0.1875rem;
    cursor: default;
    text-align: center;
    transition: all 0.15s;
    flex-shrink: 0;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
  }

  .more-bar:hover {
    border-color: #3b82f6;
    color: #3b82f6;
  }

  /* Inline add button (shown on hover, top-right) */
  .add-inline-btn {
    position: absolute;
    top: 0.125rem;
    right: 0.25rem;
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    font-weight: 600;
    color: #9ca3af;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    opacity: 0;
    z-index: 1;
  }

  .month-column:hover > .add-inline-btn {
    opacity: 1;
  }

  .events-column:hover > .add-inline-btn {
    opacity: 1;
  }

  .add-inline-btn:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #eff6ff;
  }

  /* Events row - 2/3 of space */
  .events-row {
    display: flex;
    flex: 2;
    min-height: 0;
    border-top: 1px solid #e5e7eb;
  }

  .events-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0.375rem;
    background: #faf5ff;
    border-right: 1px solid #f0e6ff;
    position: relative;
    overflow: hidden;
  }

  .events-column:last-child {
    border-right: none;
  }

  .events-column.current {
    background: #f5f3ff;
    box-shadow: inset 0 0 0 1px #c4b5fd;
  }

  .events-column.drop-target {
    background: #dbeafe !important;
    box-shadow: inset 0 0 0 2px #3b82f6 !important;
  }

  .events-area {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  /* Week group within events */
  .week-group {
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
  }

  .week-group-label {
    font-size: 0.4375rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    padding: 0.125rem 0.125rem 0;
    margin-top: 0.125rem;
  }

  .week-group:first-child .week-group-label {
    margin-top: 0;
  }

  .event-bar {
    display: flex;
    align-items: center;
    gap: 0.125rem;
    padding: 0.125rem 0.25rem;
    background: white;
    border-radius: 0.125rem;
    font-size: 0.5rem;
    position: relative;
    padding-left: 0.375rem;
    border: none;
    cursor: grab;
    text-align: left;
    min-width: 0;
    flex-shrink: 0;
  }

  .event-bar:hover {
    filter: brightness(0.95);
  }

  .event-bar.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .event-bar::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    border-radius: 0.125rem 0 0 0.125rem;
  }

  .event-bar.event-appointment::before {
    background: #60a5fa;
  }
  .event-bar.event-travel::before {
    background: #a78bfa;
  }
  .event-bar.event-event::before {
    background: #4ade80;
  }
  .event-bar.event-deadline::before {
    background: #f87171;
  }

  .event-day {
    font-weight: 600;
    color: #6b7280;
    min-width: 0.75rem;
    flex-shrink: 0;
  }

  .event-title {
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .empty-events {
    font-size: 0.5rem;
    color: #d1d5db;
    text-align: center;
    padding: 0.5rem;
  }
</style>
